import { useQueryClient } from '@tanstack/react-query';
import type { Feuille } from '../partage/api/types';
import { laFicheOuverte, laMereLue } from './ficheParSonId';
import { feuillesLues, useAllerALaFiche } from './useAllerALaFiche';
import { api } from '../partage/api/client';
import { cles } from '../partage/relectures';
import { useFeuille } from '../partage/useArbre';
import type { useNavigationDeLaFeuille } from '../projets';

/**
 * La fiche ouverte sur une feuille, et son chemin (sorti de `Feuille.tsx`, chantier
 * nettoyage-feuille, ticket 02) : le centre (« sa fiche ») ou une bulle de la feuille ; une sœur
 * du centre, atteinte par un intercalaire de sa fiche ; une bulle ouverte sur place plus bas.
 * Avec `allerALaFiche`, qui ouvre la fiche d'une bulle par son identifiant, sur place quand elle
 * est sous la feuille.
 */
export function useFicheDeLaFeuille(donnees: Feuille | undefined, navigation: ReturnType<typeof useNavigationDeLaFeuille>) {
  const client = useQueryClient();
  const ficheOuverte = navigation.fiche;

  /*
   * Les intercalaires de la fiche du centre sont SES sœurs : elles vivent sur la feuille du
   * parent, pas sur celle-ci. Sans cette feuille-là, cliquer « Conduite » depuis la fiche de
   * « Code de la route » ne trouvait la bulle nulle part et la fiche se fermait (ticket 21 de
   * finition-v1). C'est la clé que la fiche lit déjà pour dessiner ses onglets : aucune
   * requête de plus.
   */
  const feuilleDuParent = useFeuille(ficheOuverte ? (donnees?.bulle.parentId ?? '') : '');

  /*
   * Une fiche ouverte sur place plus bas que la feuille (une bulle dépliée en Hiérarchie, en
   * Liste ou sur la Carte, une étape de « Ses étapes », une ligne d'« Ici, tu peux faire ») :
   * elle vit sur la feuille de sa mère, le plus souvent déjà lue, et ses sœurs, atteintes par
   * les intercalaires, au même endroit. Sa mère se trouve dans ce qui est lu (`laMereLue`).
   */
  const mereLointaine = ficheOuverte !== null && navigation.surPlace ? laMereLue(ficheOuverte, feuillesLues(client)) : null;
  const feuilleLointaine = useFeuille(mereLointaine ?? '');

  /*
   * Une bulle d'ailleurs, ouverte sur place depuis l'étiquette d'un lien (chantier
   * liens-entre-bulles, ticket 05, décision 12) : sa propre feuille, lue au clic
   * (`ouvrirDAilleurs`), donne sa mère, qui la porte comme une bulle dépliée ; un projet n'a pas
   * de mère, sa feuille le porte en son centre. Lue seulement quand aucune mère n'est connue.
   */
  const sienne = useFeuille(ficheOuverte !== null && navigation.surPlace && mereLointaine === null ? ficheOuverte : '');

  const allerALaFiche = useAllerALaFiche({ chemin: donnees?.chemin ?? [], ouvrir: navigation.ouvrirSurPlace });

  /** La fiche d'une bulle hors de la feuille, par-dessus elle, sans la quitter. */
  const ouvrirDAilleurs = (id: string) =>
    void client.fetchQuery({ queryKey: cles.feuille(id), queryFn: () => api.feuille(id) }).then(
      () => navigation.ouvrirSurPlace(id),
      // Pas de réseau, ou elle n'existe plus : rien ne s'ouvre.
      () => undefined
    );

  const surFiche =
    ficheOuverte && donnees
      ? laFicheOuverte(
          ficheOuverte,
          donnees,
          [feuilleDuParent.data, mereLointaine ? feuilleLointaine.data : undefined],
          navigation.surPlace ? sienne.data : undefined
        )
      : null;
  return { surFiche, allerALaFiche, ouvrirDAilleurs };
}
